import { useState } from 'react';
import { CheckCircleOutlined, EditOutlined, StopOutlined } from '@ant-design/icons';
import { Alert, App, Checkbox, Form, Input, InputNumber, Modal, Switch, Tag } from 'antd';
import { api } from '../api';
import { ActiveTag, DataTable, ErrorNotice, PageHeader, Panel, RowActions } from '../components';
import { formatRupees, useLoad } from '../lib';
/** The API's body for a plan, from the form (or from a plan, to change only whether it is active). */
function planBody(values) {
    return {
        code: values.code.trim().toLowerCase(),
        kind: values.kind,
        name: values.name.trim(),
        description: (values.description ?? '').trim(),
        mrpPaise: Math.round(values.mrp * 100),
        discountPercent: values.discountPercent ?? 0,
        gstRate: values.gstRate ?? 18,
        durationMonths: values.durationMonths,
        entitlements: values.remote ? ['remote'] : [],
        active: values.active,
        sortOrder: values.sortOrder ?? 0
    };
}
function formValues(plan) {
    return {
        code: plan.code,
        kind: plan.kind ?? 'activation',
        name: plan.name,
        description: plan.description,
        mrp: (plan.mrpPaise ?? plan.pricePaise) / 100,
        discountPercent: plan.discountPercent ?? 0,
        gstRate: plan.gstRate ?? 18,
        durationMonths: plan.durationMonths,
        remote: plan.entitlements.includes('remote'),
        active: plan.active,
        sortOrder: plan.sortOrder
    };
}
/**
 * There are two plans, each only edited here: the Activation (bought once: lifetime use of the software, its months of
 * renewal included free) and the Renewal (a price per year, bought for one or more years to keep the cloud APIs on).
 */
const KINDS = { activation: 'Activation', renewal: 'Renewal' };
function months(count) {
    return count % 12 === 0 && count >= 12 ? `${count / 12} ${count === 12 ? 'year' : 'years'}` : `${count} ${count === 1 ? 'month' : 'months'}`;
}
export function PlansPage() {
    const { message } = App.useApp();
    /** The plan being edited, or null when the form is closed. */
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const watchedMrp = Form.useWatch('mrp', form) ?? 0;
    const watchedDiscount = Form.useWatch('discountPercent', form) ?? 0;
    const watchedGst = Form.useWatch('gstRate', form) ?? 18;
    const watchedKind = editing?.kind ?? 'activation';
    const previewMrp = Math.round(Number(watchedMrp || 0) * 100);
    const previewPrice = Math.round(previewMrp * (100 - Number(watchedDiscount || 0)) / 100);
    const previewTaxable = Math.round(previewPrice * 100 / (100 + Number(watchedGst || 0)));
    const previewGst = previewPrice - previewTaxable;
    const { data, error, loading, reload } = useLoad(() => api('/v1/admin/plans').then((body) => body.plans));
    const plans = [...(data ?? [])].sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'renewal' ? 1 : -1) || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    async function save(values) {
        setSaving(true);
        try {
            if (editing) {
                await api(`/v1/admin/plans/${editing.id}`, { method: 'PUT', body: JSON.stringify(planBody(values)) });
                message.success(`${values.name} plan saved`);
            }
            setEditing(null);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not save the plan');
        }
        finally {
            setSaving(false);
        }
    }
    async function setActive(plan, active) {
        try {
            await api(`/v1/admin/plans/${plan.id}`, { method: 'PUT', body: JSON.stringify(planBody({ ...formValues(plan), active })) });
            message.success(active ? `${plan.name} can be bought again` : `${plan.name} is no longer offered`);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not change the plan');
        }
    }
    return (<>
      <PageHeader title="Plans" description="Two plans, edit only. Activation is bought once: lifetime use of the software, with its first year of renewal free. Renewal is a price per year that keeps the cloud services (sync, AI reading, GST APIs, imports, remote access) running; customers can buy several years at once. Each account is signed in on one computer at a time. Prices apply to new purchases only."/>
      <ErrorNotice error={error}/>

      <Panel flush>
        <DataTable rowKey="id" loading={loading} skeletonRows={3} dataSource={plans} pagination={false} scroll={{ x: 1000 }} locale={{ emptyText: 'The Activation and Renewal plans appear here.' }} columns={[
            {
                title: 'Plan',
                skeleton: 'lines',
                render: (_value, row) => (<span>
                  <strong style={{ fontWeight: 550 }}>{row.name}</strong>
                  <span className="cell-sub mono">{row.code}</span>
                </span>)
            },
            {
                title: 'Type',
                dataIndex: 'kind',
                width: 120,
                skeleton: 'tag',
                render: (value) => <Tag color={value === 'renewal' ? 'blue' : 'green'} variant="filled">{KINDS[value] ?? KINDS.activation}</Tag>
            },
            {
                title: 'Pricing',
                dataIndex: 'pricePaise',
                width: 180,
                align: 'right',
                render: (value, row) => (<span className="num">
                  {row.discountPercent > 0 && <span className="cell-sub"><s>{formatRupees(row.mrpPaise)}</s> · {row.discountPercent}% off</span>}
                  <strong>{formatRupees(value)}</strong>
                  <span className="cell-sub">includes {row.gstRate}% GST</span>
                </span>)
            },
            { title: 'Length', dataIndex: 'durationMonths', width: 140, render: (value, row) => (row.kind === 'renewal' ? 'Per year' : `${months(value)} included`) },
            {
                title: 'Includes',
                dataIndex: 'entitlements',
                width: 150,
                skeleton: 'tag',
                render: (value) => (value.includes('remote') ? <Tag variant="filled">Remote access</Tag> : <span className="cell-sub">Local only</span>)
            },
            { title: 'Status', dataIndex: 'active', width: 120, skeleton: 'tag', render: (value) => <ActiveTag active={value} on="Offered" off="Inactive"/> },
            { title: 'Order', dataIndex: 'sortOrder', width: 80, align: 'right', render: (value) => <span className="num">{value}</span> },
            {
                title: '',
                width: 56,
                align: 'right',
                skeleton: 'actions',
                render: (_value, row) => (<RowActions label={`Actions for ${row.name}`} actions={[
                        { key: 'edit', icon: <EditOutlined />, label: 'Edit plan', onClick: () => setEditing(row) },
                        row.active
                            ? {
                                key: 'deactivate',
                                icon: <StopOutlined />,
                                label: 'Deactivate',
                                danger: true,
                                hint: 'Customers can no longer buy it',
                                confirm: {
                                    title: 'Deactivate this plan?',
                                    description: `${row.name} is no longer offered in the account panel. Customers who bought it keep their subscription until it ends.`,
                                    okText: 'Deactivate'
                                },
                                onClick: () => setActive(row, false)
                            }
                            : { key: 'activate', icon: <CheckCircleOutlined />, label: 'Activate', hint: 'Offer it in the account panel again', onClick: () => setActive(row, true) }
                    ]}/>)
            }
        ]}/>
      </Panel>

      <Modal title={`Edit ${editing?.name ?? 'plan'}`} open={editing !== null} onCancel={() => setEditing(null)} onOk={() => form.submit()} okText="Save plan" confirmLoading={saving} width={560} destroyOnHidden>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={save} initialValues={editing ? formValues(editing) : undefined}>
          <Form.Item name="kind" hidden>
            <Input/>
          </Form.Item>
          <Alert type={watchedKind === 'renewal' ? 'info' : 'success'} showIcon style={{ marginBottom: 16 }} title={watchedKind === 'renewal'
            ? 'Renewal: the price is for one year. Activated accounts buy one or more years at once (price × years) to keep the cloud services running, even long after their renewal has ended.'
            : 'Activation: bought once. The software is unlocked for life; the months below are the renewal included free.'}/>
          <div className="form-row">
            <Form.Item label="Name" name="name" rules={[{ required: true, whitespace: true, max: 80, message: 'Enter the plan name.' }]}>
              <Input placeholder="Pro" autoFocus/>
            </Form.Item>
            <Form.Item label="Code" name="code" normalize={(value) => value.toLowerCase()} rules={[{ required: true, pattern: /^[a-z0-9][a-z0-9-]{1,39}$/, message: 'Lower-case letters, digits and dashes, e.g. pro-12.' }]} extra="Kept on the subscriptions it makes.">
              <Input placeholder="pro-12" className="mono" autoComplete="off"/>
            </Form.Item>
          </div>
          <Form.Item label="Description" name="description" rules={[{ max: 2000 }]} extra="Shown to customers under the plan name.">
            <Input.TextArea rows={2} placeholder="Up to 3 computers, cloud sync and AI reading."/>
          </Form.Item>
          <div className="form-row">
            <Form.Item label={watchedKind === 'renewal' ? 'MRP per year' : 'MRP'} name="mrp" rules={[{ required: true, type: 'number', min: 1, message: 'Enter an MRP of at least ₹1.' }]} extra="Inclusive maximum retail price.">
              <InputNumber min={1} max={1000000} precision={2} step={100} prefix="₹" placeholder="12,000" style={{ width: '100%' }}/>
            </Form.Item>
            <Form.Item label="Renewal included" name="durationMonths" rules={[{ required: true }]} extra="Months included free (12 = 1 year)." hidden={watchedKind === 'renewal'}>
              <InputNumber min={1} max={60} suffix="months" placeholder="12" style={{ width: '100%' }}/>
            </Form.Item>
          </div>
          <div className="form-row">
            <Form.Item label="Discount" name="discountPercent" extra="0 keeps the MRP as the payable price.">
              <InputNumber min={0} max={95} precision={2} suffix="%" placeholder="0" style={{ width: '100%' }}/>
            </Form.Item>
            <Form.Item label="GST rate" name="gstRate" extra="GST contained in the final price.">
              <InputNumber min={0} max={28} precision={2} suffix="%" placeholder="18" style={{ width: '100%' }}/>
            </Form.Item>
          </div>
          <div className="price-preview" aria-live="polite">
            <span><small>MRP</small><strong>{formatRupees(previewMrp)}</strong></span>
            <span><small>Discount</small><strong>− {formatRupees(previewMrp - previewPrice)}</strong></span>
            <span><small>Taxable value</small><strong>{formatRupees(previewTaxable)}</strong></span>
            <span><small>GST ({Number(watchedGst || 0)}%)</small><strong>{formatRupees(previewGst)}</strong></span>
            <span className="price-preview-total"><small>{watchedKind === 'renewal' ? 'Customer pays a year' : 'Customer pays'}</small><strong>{formatRupees(previewPrice)}</strong></span>
          </div>
          <Form.Item label="Sort order" name="sortOrder" extra="Lower comes first.">
            <InputNumber min={0} max={1000} placeholder="10" style={{ width: '100%' }}/>
          </Form.Item>
          <Form.Item name="remote" valuePropName="checked" style={{ marginBottom: 12 }}>
            <Checkbox>Remote access (open the books from another computer)</Checkbox>
          </Form.Item>
          <div className="switch-row" style={{ marginBottom: 0 }}>
            <div>
              <strong>Offered</strong>
              <span>Customers can buy it in the account panel.</span>
            </div>
            <Form.Item name="active" valuePropName="checked" noStyle>
              <Switch aria-label="Offered"/>
            </Form.Item>
          </div>
          {editing && (<Alert type="info" showIcon title="Changes apply to new purchases. Customers who bought this plan keep what they paid for." style={{ marginTop: 16 }}/>)}
        </Form>
      </Modal>
    </>);
}
